import styled from '@emotion/styled'
import { Edit } from '@vivaldis/icons'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  Form,
  Input,
  Select,
  Typography
} from '@vivaldis/antd-ui'
import { Grid } from '../../../../../../../Grid'
import { Step, useStep } from '../../../../../../provider/StepProvider'
import { CountryType } from '../../../../../../types/CountryType'

interface Props {
  countries: CountryType[]
}

export const PersonalInformation = ({ countries }: Props) => {
  const [t] = useTranslation('web_ui')
  const { setStep } = useStep()

  const nationalityOptions = useMemo(
    () =>
      countries
        .map(country => ({
          label: country.nationality,
          value: country.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [countries]
  )

  const handleEditManuallyButtonClick = useCallback(() => {
    setStep(Step.EditManuallyStep)
  }, [setStep])

  return (
    <>
      <Base>
        <Typography.Title level={5} marginBottom={16}>
          {t(
            'edit_employee_personal_information_form.steps.ocr_succeeded.personal_information_header'
          )}
        </Typography.Title>

        <Button
          onClick={handleEditManuallyButtonClick}
          type="link"
          size="small"
          style={{ paddingLeft: 0, paddingRight: 0 }}
          icon={<Edit />}
        >
          {t(
            'edit_employee_personal_information_form.steps.ocr_succeeded.edit_manually_button'
          )}
        </Button>
      </Base>

      <Alert
        title={t(
          'edit_employee_personal_information_form.steps.ocr_succeeded.alert.title'
        )}
        type="info"
        style={{
          marginBottom: 16
        }}
      />

      <Grid columns={2} gap={0} columnGap={16} marginTop={6}>
        <Form.Item
          name="firstName"
          required
          label={t(
            'edit_employee_personal_information_form.steps.ocr_succeeded.first_name.label'
          )}
        >
          <Input
            name="firstName"
            placeholder={t('form.required')}
            autoComplete="off"
            disabled
          />
        </Form.Item>
        <Form.Item
          name="lastName"
          required
          label={t(
            'edit_employee_personal_information_form.steps.ocr_succeeded.last_name.label'
          )}
        >
          <Input
            name="lastName"
            placeholder={t('form.required')}
            autoComplete="off"
            disabled
          />
        </Form.Item>
      </Grid>

      <Grid columns={2} gap={0} columnGap={16} marginTop={6}>
        <Form.Item
          name="nationalityCountryId"
          required
          label={t(
            'edit_employee_personal_information_form.steps.ocr_succeeded.nationality_country_id.label'
          )}
        >
          <Select
            name="nationalityCountryId"
            placeholder={t('form.required')}
            options={nationalityOptions}
            showSearch
            autoComplete="chrome-off-employee-nationality"
            disabled
          />
        </Form.Item>
        <Form.Item
          name="ssn"
          required
          label={t(
            'edit_employee_personal_information_form.steps.ocr_succeeded.ssn.label'
          )}
        >
          <Input
            name="ssn"
            placeholder={t('form.required')}
            autoComplete="off"
            disabled
          />
        </Form.Item>
      </Grid>
    </>
  )
}

const Base = styled('div')`
  display: flex;
  justify-content: space-between;
`
